import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  InputNumber,
  Select,
  Space,
  Upload,
  message,
  Modal,
} from "antd";
import {
  CloudUploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import usePermission from "../../hooks/usePermission";
import { PartnerLabel } from "../../components/common/DeletedTag";
import StatusBadge from "../../components/common/StatusBadge";
import { listRiders } from "../../services/riders.service";
import {
  listUploadedTransactions,
  listManualTransactions,
  listAppEntries,
  createManualEntry,
  uploadTransactionsFile,
  downloadTransactionTemplate,
  updateTransaction,
  deleteTransaction,
} from "../../services/transactions.service";
import { getApiErrorMessage } from "../../utils/apiErrors";

function formatOmr(v) {
  return Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function showUploadResultModal({ totalRows, importedRows, errorRows, errors }) {
  const hasErrors = (errorRows || 0) > 0 || (errors || []).length > 0;
  Modal[hasErrors && importedRows === 0 ? "error" : hasErrors ? "warning" : "success"]({
    title: "Upload Results",
    width: 560,
    content: (
      <div>
        <p>
          <strong>Total rows:</strong> {totalRows ?? 0}
        </p>
        <p style={{ color: "#15803d" }}>
          <strong>Imported:</strong> {importedRows ?? 0}
        </p>
        <p style={{ color: errorRows ? "#b91c1c" : undefined }}>
          <strong>Failed:</strong> {errorRows ?? 0}
        </p>
        {hasErrors ? (
          <div
            style={{
              marginTop: 12,
              maxHeight: 280,
              overflow: "auto",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <p style={{ marginBottom: 8, fontWeight: 700 }}>Row errors</p>
            {(errors || []).map((err, idx) => (
              <div key={idx} style={{ marginBottom: 6, fontSize: 12 }}>
                <strong>Row {err.row}:</strong> {err.message || err.msg || "Invalid row"}
              </div>
            ))}
            {!errors?.length && errorRows > 0 ? (
              <div style={{ fontSize: 12 }}>
                Some rows failed. Check the date format (M/D/YYYY) and External Rider IDs.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    ),
  });
}

function SectionDivider({ label }) {
  return (
    <div className="tx-section-divider">
      <div className="tx-section-divider-line" />
      <div className="tx-section-divider-label">{label}</div>
      <div className="tx-section-divider-line" />
    </div>
  );
}

export default function UploadEntryTab() {
  const { canEdit, canDelete } = usePermission("transactions");
  const [form] = Form.useForm();

  const [uploadDate, setUploadDate] = useState(dayjs());
  const [riderOptions, setRiderOptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Shared day filter — the three tables always show the same business day.
  const [filterDate, setFilterDate] = useState(dayjs());

  const [uploadedRows, setUploadedRows] = useState([]);
  const [uploadedLoading, setUploadedLoading] = useState(true);
  const [uploadedPage, setUploadedPage] = useState(1);
  const [uploadedTotal, setUploadedTotal] = useState(0);

  const [appRows, setAppRows] = useState([]);
  const [appLoading, setAppLoading] = useState(true);
  const [appPage, setAppPage] = useState(1);
  const [appTotal, setAppTotal] = useState(0);

  const [manualRows, setManualRows] = useState([]);
  const [manualLoading, setManualLoading] = useState(true);
  const [manualPage, setManualPage] = useState(1);
  const [manualTotal, setManualTotal] = useState(0);

  const [editModal, setEditModal] = useState({ open: false, record: null });
  const [editForm] = Form.useForm();

  const dateParam = filterDate ? filterDate.format("YYYY-MM-DD") : undefined;

  useEffect(() => {
    listRiders({ page: 1, per_page: 100, status: "active" })
      .then((res) => {
        const records = res?.data?.records || [];
        setRiderOptions(
          records.map((r) => ({
            value: r._id,
            label: `${r.riderId || ""} — ${r.fullName || ""}`,
          }))
        );
      })
      .catch(() => {});
  }, []);

  const fetchUploaded = useCallback(async () => {
    setUploadedLoading(true);
    try {
      const res = await listUploadedTransactions({
        page: uploadedPage,
        per_page: 10,
        date: dateParam,
      });
      setUploadedRows(res?.data?.records || []);
      setUploadedTotal(res?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load uploaded transactions");
    } finally {
      setUploadedLoading(false);
    }
  }, [uploadedPage, dateParam]);

  const fetchAppEntries = useCallback(async () => {
    setAppLoading(true);
    try {
      const res = await listAppEntries({
        page: appPage,
        per_page: 10,
        date: dateParam,
      });
      setAppRows(res?.data?.records || []);
      setAppTotal(res?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load app entries");
    } finally {
      setAppLoading(false);
    }
  }, [appPage, dateParam]);

  const fetchManual = useCallback(async () => {
    setManualLoading(true);
    try {
      const res = await listManualTransactions({
        page: manualPage,
        per_page: 10,
        date: dateParam,
      });
      setManualRows(res?.data?.records || []);
      setManualTotal(res?.data?.pagination?.total_count || 0);
    } catch {
      message.error("Failed to load manual entries");
    } finally {
      setManualLoading(false);
    }
  }, [manualPage, dateParam]);

  useEffect(() => {
    fetchUploaded();
  }, [fetchUploaded]);

  useEffect(() => {
    fetchAppEntries();
  }, [fetchAppEntries]);

  useEffect(() => {
    fetchManual();
  }, [fetchManual]);

  const handleTemplate = async () => {
    try {
      const blob = await downloadTransactionTemplate();
      downloadBlob(blob, "EZYGO_Transactions_Upload_Template.csv");
    } catch {
      message.error("Template download failed");
    }
  };

  const handleUpload = async (file) => {
    if (!canEdit) {
      message.warning("You do not have permission to upload");
      return false;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (uploadDate) formData.append("uploadDate", uploadDate.format("YYYY-MM-DD"));
      const res = await uploadTransactionsFile(formData);
      const result = res?.data || res || {};
      showUploadResultModal({
        totalRows: result.totalRows,
        importedRows: result.importedRows,
        errorRows: result.errorRows,
        errors: result.errors || [],
      });
      if (result.importedRows > 0) {
        message.success(result.msg || `Imported ${result.importedRows} row(s)`);
      } else if ((result.errorRows || 0) > 0) {
        message.error(result.msg || "No rows were imported. See error details.");
      }
      setUploadedPage(1);
      fetchUploaded();
    } catch (err) {
      const msg = getApiErrorMessage(err, "Upload failed");
      Modal.error({ title: "Upload failed", content: msg });
      message.error(msg);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleSaveManual = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await createManualEntry({
        rider: values.rider,
        // Plain business day — never .toISOString(), which shifts by the browser's offset.
        transactionDate: values.transactionDate.format("YYYY-MM-DD"),
        amountCollected: values.amountCollected,
        ordersCount: values.ordersCount,
      });
      message.success("Entry saved");
      form.resetFields();
      setManualPage(1);
      fetchManual();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(getApiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (record) => {
    editForm.setFieldsValue({
      transactionDate: record.transactionDate ? dayjs(record.transactionDate) : null,
      amountCollected: record.amountCollected,
      ordersCount: record.ordersCount,
    });
    setEditModal({ open: true, record });
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      await updateTransaction(editModal.record._id, {
        transactionDate: values.transactionDate
          ? values.transactionDate.format("YYYY-MM-DD")
          : undefined,
        amountCollected: values.amountCollected,
        ordersCount: values.ordersCount,
      });
      message.success("Updated");
      setEditModal({ open: false, record: null });
      fetchUploaded();
      fetchManual();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(getApiErrorMessage(err, "Update failed"));
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete transaction?",
      content: `${record.partnerName || "Record"} — ${record.transactionDateLabel || ""}`,
      okType: "danger",
      onOk: async () => {
        try {
          await deleteTransaction(record._id);
          message.success("Deleted");
          fetchUploaded();
          fetchManual();
        } catch {
          message.error("Delete failed");
        }
      },
    });
  };

  const dateColumn = {
    title: "Date",
    dataIndex: "transactionDateLabel",
    width: 120,
    render: (v) => v || "—",
  };
  const riderColumns = [
    {
      title: "Rider ID",
      dataIndex: "partnerId",
      width: 130,
      render: (v, r) => <PartnerLabel name={v || "—"} deleted={r.riderDeleted} />,
    },
    {
      title: "Rider Name",
      dataIndex: "partnerName",
      render: (v, r) => <PartnerLabel name={v} deleted={r.riderDeleted} />,
    },
  ];

  const uploadedColumns = [
    dateColumn,
    ...riderColumns,
    { title: "Order ID", dataIndex: "orderId", render: (v) => v || "—" },
    {
      title: "Amount (OMR)",
      dataIndex: "amountCollected",
      align: "right",
      render: formatOmr,
    },
  ];

  const appColumns = [
    dateColumn,
    ...riderColumns,
    {
      title: "Orders",
      dataIndex: "ordersCount",
      width: 90,
      align: "right",
      render: (v) => (v == null ? "—" : v),
    },
    {
      title: "Amount Collected (OMR)",
      dataIndex: "amountCollected",
      align: "right",
      render: (v) => (v == null ? "—" : formatOmr(v)),
    },
    {
      title: "Amount Deposited (OMR)",
      dataIndex: "amountDeposited",
      align: "right",
      render: formatOmr,
    },
    { title: "Mode", dataIndex: "modeLabel", render: (v) => v || "—" },
    {
      title: "Status",
      key: "status",
      render: (_, r) => <StatusBadge status={r.status} label={r.statusLabel} />,
    },
  ];

  const manualColumns = [
    dateColumn,
    ...riderColumns,
    {
      title: "Total Orders",
      dataIndex: "ordersCount",
      width: 120,
      align: "right",
      render: (v) => v ?? 0,
    },
    {
      title: "Total Amount (OMR)",
      dataIndex: "amountCollected",
      align: "right",
      render: formatOmr,
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => <StatusBadge status={r.status} label={r.statusLabel} />,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
        TRANSACTION UPLOAD
      </div>
      <div style={{ marginBottom: 12, color: "#6b7280", fontSize: 13, maxWidth: 720 }}>
        Upload a CSV (Excel → Save As → CSV UTF-8). Expected columns:{" "}
        <code>Order Created On</code>, <code>Order Id</code>, <code>Rider ID</code>,{" "}
        <code>Rider Name</code>, <code>Amount</code>, <code>Rider Company Name</code>.
        Dates as <code>M/D/YYYY</code> (e.g. 7/28/2026).{" "}
        <code>Rider ID</code> must match each rider&apos;s <strong>External Rider ID</strong>.
      </div>
      <Space wrap style={{ marginBottom: 16 }}>
        <DatePicker
          value={uploadDate}
          onChange={setUploadDate}
          format="DD-MM-YYYY"
          placeholder="Upload date"
        />
        <Upload
          accept=".csv,text/csv"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={!canEdit || uploading}
        >
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={uploading}
            disabled={!canEdit}
          >
            Upload File
          </Button>
        </Upload>
        <Button icon={<DownloadOutlined />} onClick={handleTemplate}>
          Template
        </Button>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: "#6b7280" }}>Showing entries for</span>
        <DatePicker
          value={filterDate}
          onChange={(v) => {
            setUploadedPage(1);
            setAppPage(1);
            setManualPage(1);
            setFilterDate(v);
          }}
          format="DD-MM-YYYY"
          allowClear
          placeholder="All dates"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchUploaded();
            fetchAppEntries();
            fetchManual();
          }}
        >
          Refresh
        </Button>
      </Space>

      <SectionDivider label="Uploaded Orders (Admin Dashboard)" />
      <DataTable
        columns={uploadedColumns}
        dataSource={uploadedRows}
        loading={uploadedLoading}
        pagination={{ current: uploadedPage, pageSize: 10, total: uploadedTotal }}
        onPageChange={(p) => setUploadedPage(p)}
        actions={{
          onEdit: canEdit ? openEdit : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
        scroll={{ x: 900 }}
      />

      <SectionDivider label="App Entries (Submitted by Riders)" />
      <DataTable
        columns={appColumns}
        dataSource={appRows}
        loading={appLoading}
        pagination={{ current: appPage, pageSize: 10, total: appTotal }}
        onPageChange={(p) => setAppPage(p)}
        scroll={{ x: 1000 }}
      />

      <SectionDivider label="Manual Entry" />

      <Form
        form={form}
        layout="vertical"
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <Form.Item
            name="transactionDate"
            label="Date"
            rules={[{ required: true, message: "Date required" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item
            name="rider"
            label="Rider ID"
            rules={[{ required: true, message: "Select rider" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={riderOptions}
              placeholder="Select Rider ID"
            />
          </Form.Item>
          <Form.Item
            name="amountCollected"
            label="Total Amount (OMR)"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.001} precision={3} />
          </Form.Item>
          <Form.Item
            name="ordersCount"
            label="Total Number of Orders"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </div>
        <Space>
          <Button onClick={() => form.resetFields()}>Cancel</Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!canEdit}
            onClick={handleSaveManual}
          >
            Save Entry
          </Button>
        </Space>
      </Form>

      <DataTable
        columns={manualColumns}
        dataSource={manualRows}
        loading={manualLoading}
        pagination={{ current: manualPage, pageSize: 10, total: manualTotal }}
        onPageChange={(p) => setManualPage(p)}
        actions={{
          onEdit: canEdit ? openEdit : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title="Edit Transaction"
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, record: null })}
        onOk={handleEditSave}
        okText="Save"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="transactionDate" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
          </Form.Item>
          <Form.Item
            name="amountCollected"
            label="Total Amount (OMR)"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.001} precision={3} />
          </Form.Item>
          <Form.Item name="ordersCount" label="Total Number of Orders">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
